import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList, ArrowLeft, Ship } from 'lucide-react';
import { api } from '../lib/api';
import { hidesLinkedShipments } from '../lib/linkedShipments.mjs';
import { usd, fmtNum, StatusChip, ErrorBox, Field, Labeled, inputCls, btnCls, btn2Cls } from '../lib/q2c';
import { OrderBilling } from './InvoicesPage';

export default function OrdersPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('active');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current; // only the latest filter's response may update the list
    try { const r = await api.get(`/orders?status=${status}`); if (mine === seq.current) { setRows(r.orders || []); setError(''); setLoaded(true); } } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><ClipboardList size={20} /> Orders</h1>
          <p className="text-sm text-[var(--text-secondary)]">Accepted quotes become orders here (Quotes → Convert to order).</p>
        </div>
        <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
          {[['active', 'Active'], ['delivered', 'Delivered'], ['all', 'All']].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs font-medium ${status === k ? 'bg-primary text-white' : 'bg-white text-[var(--text-secondary)] dark:bg-slate-800'}`}>{l}</button>
          ))}
        </div>
      </div>
      <ErrorBox msg={error} />
      <div className="space-y-2">
        {rows.map((o) => (
          <Link key={o.id} to={`/orders/${o.id}`} className="block rounded-xl border border-[var(--border)] bg-white p-4 hover:border-primary dark:bg-slate-900">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-[var(--text-primary)]">{o.title}</p>
                <p className="text-xs text-[var(--text-secondary)]">{o.number} · {o.customer_name || '—'}{o.customer_po ? ` · PO ${o.customer_po}` : ''}{o.concria_order_ref ? ` · Concria ${o.concria_order_ref}` : ''}</p>
              </div>
              <div className="text-right"><p className="font-semibold">{usd(o.total)}</p><StatusChip status={o.status} /></div>
            </div>
            <p className="mt-2 text-xs text-[var(--text-secondary)]">
              Sales Lead {(o.sales_lead_email || '—').split('@')[0]} · Delivery Lead {o.delivery_lead_email ? o.delivery_lead_email.split('@')[0] : 'none'}{o.shipment_id ? ' · shipment linked' : ' · no shipment'}
            </p>
          </Link>
        ))}
        {loaded && !error && !rows.length && <p className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-secondary)]">No orders.</p>}
      </div>
    </div>
  );
}

const EDIT_KEYS = ['title', 'customer_po', 'concria_order_ref', 'need_by_date', 'status', 'sales_lead_email', 'delivery_lead_email', 'billing_email', 'notes'];

export function OrderDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [shipments, setShipments] = useState([]);
  const [loadNotes, setLoadNotes] = useState([]);
  const [linkId, setLinkId] = useState('');
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);
  const routeId = useRef(id);
  routeId.current = id;
  const load = useCallback(async () => {
    if (id !== routeId.current) return; // an action's reload for an order we've left does nothing
    const mine = ++seq.current;
    try {
      const d = await api.get(`/orders/${id}`);
      if (mine !== seq.current || d.order.id !== routeId.current) return; // a newer load (or another order) won
      setData(d); setF(Object.fromEntries(EDIT_KEYS.map((k) => [k, d.order[k] ?? '']))); setError('');
    } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [id]);
  // Navigating to another order: clear the old one first so no stale form or action stays live.
  useEffect(() => { setData(null); setF(null); load(); }, [load]);
  // Supporting lists load once for the detail page.
  // Keep either failure visible so empty controls are not mistaken for complete data.
  const noteLoad = (text) => setLoadNotes((n) => (n.includes(text) ? n : [...n, text]));
  useEffect(() => {
    api.get('/orders/meta').then(setMeta).catch(() => noteLoad('Could not load the partner and status lists. Reload the page before changing the Sales Lead, Delivery Lead or Status.'));
    // No access to shipments (403) or no shipments module (503) is not a failed load.
    api.get('/shipments?status=all').then((r) => setShipments(r.shipments || [])).catch((e) => { if (!hidesLinkedShipments(e)) noteLoad('Could not load the shipments to link. Reload the page to try again.'); });
  }, []);
  if (!data || !f) return <div className="mx-auto max-w-5xl"><ErrorBox msg={error} />{!error && 'Loading…'}</div>;
  const o = data.order;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Shipment actions reload the order: with unsaved edits they wait (save or discard first).
  const dirty = EDIT_KEYS.some((k) => String(f[k] ?? '') !== String(o[k] ?? ''));
  const shipBlocked = saving || dirty;
  async function save() {
    setSaving(true); setError(''); setNotice('');
    try {
      const body = {};
      for (const k of EDIT_KEYS) if (String(f[k] ?? '') !== String(o[k] ?? '')) body[k] = f[k] === '' ? null : f[k];
      if (!Object.keys(body).length) return;
      await api.patch(`/orders/${id}`, { ...body, revision: o.updated_at }); await load(); setNotice('Saved.');
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }
  async function shipment(body) {
    setSaving(true); setError(''); setNotice('');
    try {
      const r = await api.post(`/orders/${id}/shipment`, { ...body, revision: o.updated_at });
      // eslint-disable-next-line no-alert
      if (r.warnings?.length) window.alert(r.warnings.join('\n'));
      await load();
      // The picker reflects the change, and a good list retires the earlier load note.
      api.get('/shipments?status=all').then((x) => { setShipments(x.shipments || []); setLoadNotes((n) => n.filter((t) => !t.startsWith('Could not load the shipments to link.'))); }).catch(() => {});
      setNotice('Shipment updated.');
    } catch (e) { await load(); setError(e.message); } finally { setSaving(false); }
  }
  const partners = meta?.partners || [];
  return (
    <div className="mx-auto max-w-5xl pb-16">
      <Link to="/orders" className="mb-2 inline-flex items-center gap-1 text-sm text-[var(--text-secondary)]"><ArrowLeft size={14} /> Orders</Link>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">{o.number} · {o.title}</h1>
          <p className="text-sm text-[var(--text-secondary)]">{o.customer_name}{data.quote ? <> · from <Link className="text-primary" to={`/quotes/${data.quote.id}`}>{o.quote_ref}</Link> (accepted by {data.quote.accepted_by_name})</> : null}</p>
        </div>
        <div className="text-right"><p className="text-lg font-semibold">{usd(o.total)}</p><StatusChip status={o.status} /></div>
      </div>
      <ErrorBox msg={error} />
      {loadNotes.map((n) => <p key={n} className="mb-3 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">{n}</p>)}
      {notice && <p className="mb-3 rounded bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

      <fieldset disabled={saving} className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-[var(--border)] bg-white p-4 sm:grid-cols-2 lg:grid-cols-3 dark:bg-slate-900">
        <Labeled t="Customer PO"><input className={inputCls} value={f.customer_po} onChange={set('customer_po')} /></Labeled>
        <Labeled t="Concria order / offer ref"><input className={inputCls} value={f.concria_order_ref} onChange={set('concria_order_ref')} /></Labeled>
        <Labeled t="Status"><select className={inputCls} value={f.status} onChange={set('status')}>{(meta?.statuses || [o.status]).map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></Labeled>
        <Labeled t="Sales Lead (fee 30%)"><select className={inputCls} value={f.sales_lead_email} onChange={set('sales_lead_email')}><option value="">—</option>{partners.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}</select></Labeled>
        <Labeled t="Delivery Lead (fee 20%)"><select className={inputCls} value={f.delivery_lead_email} onChange={set('delivery_lead_email')}><option value="">None</option>{partners.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}</select></Labeled>
        <Labeled t="Invoices go to"><input className={inputCls} type="email" value={f.billing_email} onChange={set('billing_email')} /></Labeled>
        <Labeled t="Need on site by"><input className={inputCls} type="date" value={f.need_by_date} onChange={set('need_by_date')} /></Labeled>
        <Labeled t="Title"><input className={inputCls} value={f.title} onChange={set('title')} /></Labeled>
        <Labeled t="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={set('notes')} /></Labeled>
        <div className="sm:col-span-2 lg:col-span-3"><button className={btnCls} onClick={save}>{saving ? 'Saving…' : 'Save order'}</button></div>
      </fieldset>

      <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Ship size={15} /> Shipment</h2>
        {dirty && <p className="mb-2 text-xs text-amber-700">Save the order edits above before changing the shipment.</p>}
        {data.shipment ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Shipment"><Link className="text-primary" to={`/shipments/${data.shipment.id}`}>{data.shipment.order_ref}</Link></Field>
              <Field label="Now" value={data.shipment.current_milestone || 'no update yet'} />
              <Field label="ETA" value={data.shipment.eta || '—'} />
              <Field label="Need by" value={data.shipment.need_by_date || '—'} />
            </div>
            <button className={btn2Cls} disabled={shipBlocked} onClick={() => shipment({ shipment_id: null })}>Unlink</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <button className={btnCls} disabled={shipBlocked} onClick={() => shipment({ create: true })}>Create shipment</button>
            <span className="text-xs text-[var(--text-secondary)]">or link</span>
            <select className={`${inputCls} w-full sm:max-w-xs`} disabled={shipBlocked} value={linkId} onChange={(e) => setLinkId(e.target.value)}><option value="">Choose a shipment…</option>{shipments.map((s) => <option key={s.id} value={s.id}>{s.order_ref} · {s.project_name || s.customer_name || ''}</option>)}</select>
            <button className={btn2Cls} disabled={!linkId || shipBlocked} onClick={() => shipment({ shipment_id: linkId })}>Link</button>
          </div>
        )}
      </section>

      <OrderBilling key={`${o.id}-${o.shipment_id || ''}`} orderId={o.id} />

      <section className="rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
        <h2 className="mb-2 text-sm font-semibold">Lines (from the accepted quote)</h2>
        <div className="overflow-x-auto"><table className="w-full text-sm sm:min-w-[560px]"><tbody>
          {(o.lines || []).map((l) => (
            <tr key={l.id} className={`border-t border-[var(--border)] ${l.optional ? 'text-[var(--text-secondary)]' : ''}`}>
              <td className="break-words py-1.5 pr-2">{l.description}{l.optional ? ' (optional)' : ''}
                <span className="block text-xs text-[var(--text-secondary)] sm:hidden">{l.pricing === 'lump' ? '1' : `${fmtNum(l.qty, 0, 4)} ${l.unit || ''}`} × ${fmtNum(l.unit_price, 2, 4)}{l.pricing === 'per_sf' ? '/sf' : ''}</span></td>
              <td className="hidden text-right sm:table-cell">{l.pricing === 'lump' ? '1' : `${fmtNum(l.qty, 0, 4)} ${l.unit || ''}`}</td>
              <td className="hidden text-right sm:table-cell">${fmtNum(l.unit_price, 2, 4)}{l.pricing === 'per_sf' ? '/sf' : ''}</td>
              <td className="whitespace-nowrap text-right align-top font-medium">{usd(l.amount)}</td>
            </tr>
          ))}
          <tr className="border-t border-[var(--border)] font-semibold"><td className="py-1.5 pr-2 text-right sm:hidden">Total</td><td colSpan={3} className="hidden py-1.5 text-right sm:table-cell">Total</td><td className="whitespace-nowrap text-right">{usd(o.total)}</td></tr>
        </tbody></table></div>
      </section>
    </div>
  );
}
